/**
 * admin-svc /admin/did-routes — assign/remove Twilio DIDs to agents (V048).
 *
 * A DID (Direct Inward Dialing number) links a Twilio phone number to a
 * specific agent+tenant so inbound calls route to the right voice-agent
 * context. Each Twilio number maps to exactly one agent; an agent has at
 * most one DID per tenant.
 */

import type { ApiClient } from "../client";

export interface AgentDID {
  id: string;
  tenant_id: string;
  agent_id: string;
  user_id: string;
  did: string;
  label: string | null;
  /** A vanity spelling ("+1 25-MYHOME-24") shown instead of the raw digits
   * above — the tel: link still dials `did` itself (V070). */
  display_text: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  /** This route belongs to the agent's OTHER brokerage — shown so both sides
   *  know the agent is reachable, editable only by the side that owns it.
   *  A DID is never duplicated: one number routes to exactly one place. */
  foreign?: boolean;
  /** Whose it is, for "Managed by …". Null on your own routes. */
  owner_brokerage_name?: string | null;
  /** On a foreign route, THIS tenant's `users.id` for the same person —
   *  `user_id`/`agent_id` above are the other brokerage's rows and match
   *  nothing on this roster. */
  matched_user_id?: string | null;
}

/** `agentId` directly, or `userId` (what the Staff roster holds) + `tenantId`
 * to disambiguate a dual-licensed user's two agent rows — same translation
 * `agentRateCard` does. */
export interface DIDCreateTarget {
  agentId?: string;
  userId?: string;
  tenantId?: string;
}

export const didRoutesService = {
  list(client: ApiClient, tenantId?: string) {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
    return client.request<AgentDID[]>(`/admin/did-routes${qs}`);
  },

  create(
    client: ApiClient,
    did: string,
    target: DIDCreateTarget,
    label?: string,
    displayText?: string,
  ) {
    return client.request<AgentDID>("/admin/did-routes", {
      method: "POST",
      body: {
        did,
        label: label || null,
        display_text: displayText || null,
        agent_id: target.agentId,
        user_id: target.userId,
        tenant_id: target.tenantId,
      },
    });
  },

  update(
    client: ApiClient,
    didId: string,
    body: { label?: string | null; display_text?: string | null },
  ) {
    return client.request<AgentDID>(`/admin/did-routes/${didId}`, {
      method: "PATCH",
      body,
    });
  },

  remove(client: ApiClient, didId: string) {
    return client.request<void>(`/admin/did-routes/${didId}`, {
      method: "DELETE",
    });
  },
};
